import { PhoneNumberInputAdapter } from '@/app/[lang]/(main)/components/shared/form/PhoneNumberRegistration/adapter';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { useModals } from '@/contexts/ModalContext';
import { useTelegramUsersConfirmContacts, useTelegramUsersPatchPhone } from '@/generated';
import { useUpdateGlobalUser } from '@/hooks/useUpdateGlobalUser';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import { useCountdown } from 'usehooks-ts';

type Props = {
  onChange?: (newPhone: string) => void;
};

type Phone = {
  countryId: string;
  prefix: string;
  phoneNumber: string;
};

const OTP_LENGTH = 4;

export const SetPhoneModal: React.FC<Props> = ({ onChange }) => {
  const modals = useModals();
  const t = useTranslations('modals.setPhoneModal');
  const restorationT = useTranslations('restoration');

  const [stage, setStage] = useState<'enter' | 'code' | 'final'>('enter');
  const [phone, setPhone] = useState<Phone>({ countryId: '', prefix: '', phoneNumber: '' });
  const [otpId, setOtpId] = useState('');

  const [errorText, setErrorText] = useState('');

  const [isLoading, setIsLoading] = useState(false);

  const [count, { startCountdown }] = useCountdown({
    countStart: 60,
    intervalMs: 1000,
  });

  const [otp, setOtp] = useState('');

  const { mutateAsync: updatePhone } = useTelegramUsersPatchPhone();
  const { mutateAsync: sendCode } = useTelegramUsersConfirmContacts();
  const updateGlobalUser = useUpdateGlobalUser();

  const onClose = useCallback(() => {
    modals.close(`set-phone-modal`);
  }, [modals]);

  const onPhoneInputChange = useCallback(
    (newCountryId: string, newPhoneNumber: string, newPrefix?: string) => {
      setPhone({
        countryId: newCountryId,
        phoneNumber: newPhoneNumber,
        prefix: newPrefix || '',
      });
    },
    [setPhone],
  );

  const getErrorText = useCallback(
    (e: any) => {
      const code = e?.data?.code;
      return code && t.has(`errors.${code}`) ? t(`errors.${code}`) : restorationT('something_went_wrong');
    },
    [restorationT, t],
  );

  const goToOtp = useCallback(async () => {
    setErrorText('');
    setIsLoading(true);
    try {
      const res = await updatePhone({
        data: {
          countryId: phone.countryId,
          phone: phone.prefix + phone.phoneNumber,
        },
      });
      if (res.success) {
        startCountdown();
        setOtpId(res.id);
        setStage('code');
        setIsLoading(false);
      }
    } catch (e: any) {
      setErrorText(getErrorText(e));
      setIsLoading(false);
    }
  }, [phone, updatePhone, startCountdown, getErrorText]);

  const onCodeEntered = useCallback(async () => {
    setIsLoading(true);
    try {
      await sendCode({
        data: {
          code: otp,
          id: otpId,
        },
      });
      setStage('final');
      setIsLoading(false);
    } catch (e: any) {
      setErrorText(getErrorText(e));
      setIsLoading(false);
    }
  }, [otp, otpId, sendCode, getErrorText]);

  useEffect(() => {
    if (otp.replace(' ', '').length >= OTP_LENGTH) {
      onCodeEntered();
    }
  }, [otp, onCodeEntered]);

  const onFinish = useCallback(() => {
    onChange?.(phone.prefix + phone.phoneNumber);
    updateGlobalUser();
    onClose();
  }, [onChange, onClose, phone, updateGlobalUser]);

  const phoneEntering = (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (phone.countryId && phone.phoneNumber && phone.prefix && !isLoading) {
          void goToOtp();
        }
      }}
      className='flex w-full flex-col items-center gap-[16px]'
    >
      <DialogTitle className='text-xl-heading text-center leading-normal'>{t('phone_change')}</DialogTitle>

      <div className='text-m text-center'>{t('set_phone')}</div>

      <div className='text-m text-center'>{t('country_code')}</div>

      <PhoneNumberInputAdapter
        autoFocus
        countryId={phone.countryId}
        phoneNumber={phone.phoneNumber}
        onChange={onPhoneInputChange}
        shouldAutoFetchCountry
      />

      {errorText && <div className='text-s text-red-day text-center'>{errorText}</div>}

      <div className='text-center text-[14px]! text-text-disabled-day'>{t('sms_info')}</div>

      <Button
        type='submit'
        variant='default'
        disabled={!(phone.countryId && phone.phoneNumber && phone.prefix)}
        className='h-auto disabled:bg-primary-stroke-day disabled:text-text-disabled-day w-full rounded-[12px] py-[10px] text-white text-m'
      >
        {t('next')}
      </Button>
    </form>
  );

  const codeEntering = (
    <>
      <DialogTitle className='text-xl-heading text-center leading-normal'>{t('phone_change')}</DialogTitle>

      <div className='text-m text-center'>
        {t('sms_sent')}
        <br />
        {phone.prefix + phone.phoneNumber}
      </div>

      <div className='flex flex-col gap-[16px]'>
        <div className='text-m text-center font-medium'>{t('enter_code')}</div>
        <div className='flex justify-center w-full'>
          <InputOTP autoFocus maxLength={OTP_LENGTH} value={otp} onChange={setOtp} inputMode='numeric'>
            <InputOTPGroup className='gap-[8px]'>
              {Array.from({ length: OTP_LENGTH }).map((_, index) => (
                <InputOTPSlot
                  key={index}
                  index={index}
                  aria-invalid={!!errorText}
                  className='w-[40px]! h-[40px]! rounded-[8px]! first:rounded-s-[8px]! last:rounded-e-[8px]! border! bg-transparent! text-[16px] shadow-none data-[active=true]:ring-0! data-[active=true]:border-main-green!'
                />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>
        {errorText && <div className='text-s text-red-day text-center'>{errorText}</div>}
      </div>

      <>
        <div className='text-xs text-text-disabled-day text-center'>{t('sms_delay_info')}</div>

        {count > 0 ? (
          <div className='text-m text-primary text-center'>
            {t('resend_code_info')}
            <br />
            {count} {t('seconds')}
          </div>
        ) : (
          <Button
            variant='ghost'
            onClick={() => {
              goToOtp();
            }}
            className='h-auto text-primary-green-day hover:bg-transparent w-full rounded-[12px] py-[10px] text-m'
          >
            {t('resend_code')}
          </Button>
        )}
      </>
    </>
  );

  const final = (
    <>
      <DialogTitle className='text-xl-heading text-center leading-normal'>{t('phone_has_changed')}</DialogTitle>
      <div className='text-m text-center'>
        {t('new_phone_info', {
          phone: phone.prefix + phone.phoneNumber,
        })}
      </div>

      <Button
        variant='outline'
        onClick={() => {
          onFinish();
        }}
        className='h-auto text-primary-green-day bg-transparent hover:bg-transparent border-primary-green-day w-full rounded-[12px] py-[10px] text-m'
      >
        {t('finish')}
      </Button>
    </>
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        aria-describedby={undefined}
        className='max-w-[375px] sm:max-w-[375px] p-0 rounded-[24px] bg-bg-day'
      >
        <div className='flex flex-col gap-[16px] p-[16px] w-full items-center'>
          {isLoading ? (
            <div className='w-full h-full min-h-[250px] flex items-center justify-center'>
              <Spinner className='size-[36px] text-primary-green-day' />
            </div>
          ) : (
            <>
              {stage === 'enter' && phoneEntering}

              {stage === 'code' && codeEntering}

              {stage === 'final' && final}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
